/**
 * <image-slider> Custom Element — Phase 6
 *
 * Replaces theme/js/modules/sliders.js (Slick + jQuery IIFE).
 *
 * Markup contract:
 *   <image-slider role="region" aria-roledescription="carousel" aria-label="…">
 *     <div class="image-slider-track" tabindex="0" aria-label="…">
 *       <div class="slide" aria-roledescription="slide">…</div> …
 *     </div>
 *     <div class="image-slider-dots"></div>
 *     <div class="image-slider-arrows">…</div>
 *     <span class="image-slider__live" aria-live="polite"></span>
 *   </image-slider>
 *
 * The inner `.image-slider-track` is the horizontal scroll container
 * (mobile/medium), so the dots/arrows on the outer don't scroll away
 * with the slides on swipe.
 *
 * Mobile/medium (<1024px): native horizontal scroll-snap. User swipes
 * or arrow-keys the focused track; an IntersectionObserver updates the
 * active dot when a slide settles. Tapping a dot scrolls the new slide
 * into view via track.scrollTo (so the page itself doesn't move).
 *
 * Desktop (>=1024px): slides are absolutely-positioned and stacked; the
 * outer's height is set to the active slide's height. Crossfades use
 * WAAPI (parallel opacity + height keyframes) for adaptive height.
 *
 * Accessibility:
 *   - region landmark with aria-label + aria-roledescription
 *   - aria-live polite span announces slide changes
 *   - inert + aria-hidden on inactive slides on desktop (inert blocks
 *     focus, so keyboard users can't tab into hidden content; aria-hidden
 *     belt-and-suspenders for engines that haven't implemented inert yet)
 *   - prefers-reduced-motion respected: WAAPI duration → 0, scrollTo
 *     uses 'auto' instead of 'smooth'
 *   - ←/→ arrow keys advance/retreat when focus is inside the slider
 *   - dot buttons + track + arrow buttons are all keyboard-focusable;
 *     dot tap targets meet WCAG 2.5.8 via SCSS
 *   - i18n: every JS-built string (slide labels, button labels, live
 *     announcements) is read from app.get('i18n.a11y.slider.*')
 *
 * Self-upgrades on connect — no init() call. Preserves the legacy
 * .slick-initialized / .slick-active hooks so existing CSS selectors
 * and E2E assertions keep passing without churn (the names are inert;
 * Slick itself is gone).
 *
 * AI: Entirely written by AI
 * Date: 2026-04-26
 * Model: Claude Opus 4.7
 *
 * [ImageSlider]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { getEasing } from './easing.js';

const SLIDE_DURATION = 450;
const TRACK_SELECTOR = ':scope > .image-slider-track';
const SLIDE_SELECTOR = ':scope > .image-slider-track > .slide';
const DOTS_SELECTOR = '.image-slider-dots';
const ARROWS_SELECTOR = '.image-slider-arrow';
const LIVE_SELECTOR = '.image-slider__live';

// ─── Slick compatibility hooks ───────────────────────────────────────────
// Slick is gone (Phase 6) but the class names live on as inert markers
// that existing CSS rules and E2E selectors still anchor on.
//   .slick-initialized — added once the custom element finishes setup
//   .slick-active      — toggled on the active dot's <li>
const INIT_CLASS = 'slick-initialized';
const ACTIVE_DOT_CLASS = 'slick-active';

// Module-level singletons. With multiple sliders on a page (product
// mobile + store details + article), one MediaQueryList shared across
// instances is enough; per-instance change handlers are attached
// against the same MQL.
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');
const REDUCED_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

// Default a11y strings used when the i18n bundle hasn't hydrated (e.g.
// a module loads before app-config). These are English fallbacks; the
// real values come from theme/locales/*.json via app.get('i18n.a11y.*').
const I18N_FALLBACK = {
  carousel: 'carousel',
  slide: 'slide',
  slide_label: '{n} of {total}',
  go_to_slide: 'Go to slide {n}',
  change_announcement: 'Slide {n} of {total}',
};

const EASING_PATH = 'cssEasing.easeInOutCubic';
const EASING_FALLBACK = 'cubic-bezier(0.4, 0, 0.2, 1)';

function t(key, vars) {
  const template = appGet(`i18n.a11y.slider.${key}`) || I18N_FALLBACK[key];
  if (!template) return '';
  if (!vars) return template;
  return Object.keys(vars).reduce(
    (str, k) => str.replaceAll(`{${k}}`, vars[k]),
    template
  );
}

class ImageSlider extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains(INIT_CLASS)) return;

    this._track = this.querySelector(TRACK_SELECTOR);
    this._slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
    if (!this._track || this._slides.length === 0) return;

    this._currentIndex = 0;
    this._isAnimating = false;
    this._dotsContainer = this.querySelector(DOTS_SELECTOR);
    this._arrows = Array.from(this.querySelectorAll(ARROWS_SELECTOR));
    this._liveRegion = this.querySelector(LIVE_SELECTOR);
    this._dotClickHandlers = [];

    this._applyA11yAttributes();
    this._buildDots();
    this._wireArrows();
    this._wireKeyboard();
    this._wireBreakpointWatch();
    this._applyLayoutForBreakpoint();
    this._observeMobileScroll();
    this._wireImageLoadResize();

    this.classList.add(INIT_CLASS);
    log('[ImageSlider] initialized', { slides: this._slides.length });
  }

  disconnectedCallback() {
    if (this._desktopHandler) {
      DESKTOP_MQ.removeEventListener('change', this._desktopHandler);
    }
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
      this._intersectionObserver = null;
    }
    if (this._handleArrow) {
      this._arrows.forEach((btn) => btn.removeEventListener('click', this._handleArrow));
    }
    if (this._handleKeydown) {
      this.removeEventListener('keydown', this._handleKeydown);
    }
    if (this._dotClickHandlers && this._dotButtons) {
      this._dotButtons.forEach((btn, i) => {
        if (btn && this._dotClickHandlers[i]) {
          btn.removeEventListener('click', this._dotClickHandlers[i]);
        }
      });
    }
  }

  // ─── Setup ────────────────────────────────────────────────────────────

  _applyA11yAttributes() {
    // Region label / roledescription are set in Liquid (so SSR is
    // already accessible). Per-slide labels and the inert/aria-hidden
    // flip live in JS because they depend on the active index.
    const total = this._slides.length;
    this._slides.forEach((slide, i) => {
      slide.setAttribute('aria-label', t('slide_label', { n: i + 1, total }));
    });
  }

  _buildDots() {
    if (!this._dotsContainer) return;
    if (this._slides.length <= 1) {
      this._dotsContainer.style.display = 'none';
      return;
    }
    const ul = document.createElement('ul');
    this._slides.forEach((_, i) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', t('go_to_slide', { n: i + 1 }));
      if (i === 0) button.setAttribute('aria-current', 'true');
      const handler = () => this.goTo(i);
      this._dotClickHandlers[i] = handler;
      button.addEventListener('click', handler);
      li.appendChild(button);
      if (i === 0) li.classList.add(ACTIVE_DOT_CLASS);
      ul.appendChild(li);
    });
    this._dotsContainer.replaceChildren(ul);
    this._dots = Array.from(ul.children);
    this._dotButtons = this._dots.map((li) => li.querySelector('button'));
  }

  _wireArrows() {
    if (this._slides.length <= 1) {
      this._arrows.forEach((btn) => {
        btn.style.display = 'none';
      });
      return;
    }
    this._handleArrow = (e) => {
      const dir = e.currentTarget.getAttribute('data-direction');
      this._step(dir === 'next' ? 1 : -1);
    };
    this._arrows.forEach((btn) => btn.addEventListener('click', this._handleArrow));
  }

  // Keyboard arrow nav matches the WAI-ARIA APG carousel pattern.
  // ←/→ advance only when focus is inside the carousel — so a global
  // arrow-key handler elsewhere on the page doesn't fight us.
  _wireKeyboard() {
    if (this._slides.length <= 1) return;
    this._handleKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      // Don't hijack arrow keys inside a focused interactive element
      // that has its own arrow-key semantics (text input, etc.).
      const target = event.target;
      if (
        target &&
        target.matches('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      // Mobile: let the native scroll-snap handle ← / → on the track
      // so the browser does the smooth (or instant, per reduced-motion)
      // snap and keeps the IntersectionObserver in charge of the dot.
      if (!DESKTOP_MQ.matches && target === this._track) return;
      event.preventDefault();
      this._step(event.key === 'ArrowRight' ? 1 : -1);
    };
    this.addEventListener('keydown', this._handleKeydown);
  }

  _wireBreakpointWatch() {
    this._desktopHandler = () => this._applyLayoutForBreakpoint();
    DESKTOP_MQ.addEventListener('change', this._desktopHandler);
  }

  _applyLayoutForBreakpoint() {
    if (DESKTOP_MQ.matches) {
      this._slides.forEach((slide, i) => {
        const isActive = i === this._currentIndex;
        slide.style.opacity = isActive ? '1' : '0';
        this._setSlideHidden(slide, !isActive);
      });
      this._setHeightFromSlide(this._slides[this._currentIndex], { animate: false });
    } else {
      // Mobile: every slide is reachable in the scroll viewport, so
      // none should be inert / aria-hidden.
      this._slides.forEach((slide) => {
        slide.style.opacity = '';
        this._setSlideHidden(slide, false);
      });
      this.style.height = '';
    }
  }

  // Use `inert` to block focus + interaction on inactive slides; a slide
  // with focusable children (a play button, a link) would otherwise let
  // a Tab user reach hidden content. aria-hidden alone doesn't block
  // focus — that's the classic carousel a11y trap. Keep aria-hidden as
  // belt-and-suspenders for engines that don't implement inert (Safari
  // pre-15.5, older Firefox).
  _setSlideHidden(slide, hidden) {
    // Skip the DOM write when state already matches — _setActiveIndex
    // calls this for every slide on every navigation, and N-1 of them
    // are no-ops (only the outgoing + incoming slides change state).
    if (slide.hasAttribute('inert') === hidden) return;
    if (hidden) {
      slide.setAttribute('inert', '');
      slide.setAttribute('aria-hidden', 'true');
    } else {
      slide.removeAttribute('inert');
      slide.setAttribute('aria-hidden', 'false');
    }
  }

  // Lazy-loaded images can change the slider's height after init. Recalculate
  // the desktop height once each pending image finishes loading.
  _wireImageLoadResize() {
    const imgs = this.querySelectorAll('img');
    imgs.forEach((img) => {
      if (img.complete && img.naturalHeight !== 0) return;
      const onLoad = () => {
        if (!DESKTOP_MQ.matches) return;
        this._setHeightFromSlide(this._slides[this._currentIndex], { animate: false });
      };
      img.addEventListener('load', onLoad, { once: true });
      img.addEventListener('error', onLoad, { once: true });
    });
  }

  _setHeightFromSlide(slide, { animate, duration = SLIDE_DURATION, easing } = {}) {
    if (!slide) return Promise.resolve();
    const newH = slide.scrollHeight || slide.offsetHeight;
    if (!newH) return Promise.resolve();
    const oldH = this.offsetHeight;
    if (!animate || REDUCED_MOTION_MQ.matches || oldH === 0 || oldH === newH) {
      this.style.height = `${newH}px`;
      return Promise.resolve();
    }
    const anim = this.animate(
      [{ height: `${oldH}px` }, { height: `${newH}px` }],
      { duration, easing: easing || getEasing(EASING_PATH, EASING_FALLBACK), fill: 'forwards' }
    );
    return anim.finished.then(() => {
      this.style.height = `${newH}px`;
      anim.cancel();
    });
  }

  // ─── Mobile scroll-snap ──────────────────────────────────────────────

  _observeMobileScroll() {
    if (typeof IntersectionObserver === 'undefined') return;
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (DESKTOP_MQ.matches) return;
        let best = null;
        for (const entry of entries) {
          if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
            best = entry;
          }
        }
        if (!best) return;
        const idx = this._slides.indexOf(best.target);
        if (idx !== -1 && idx !== this._currentIndex) {
          this._setActiveIndex(idx);
        }
      },
      { root: this._track, threshold: [0.5, 0.75, 1] }
    );
    this._slides.forEach((slide) => this._intersectionObserver.observe(slide));
  }

  // ─── Navigation ──────────────────────────────────────────────────────

  // Wrap-around step. Slick's mobile config was `infinite: false`; keeping
  // the wrap consistent across breakpoints feels better than the old
  // dead-end at the last slide and matches desktop's `infinite: true`.
  _step(delta) {
    const len = this._slides.length;
    const next = (this._currentIndex + delta + len) % len;
    this.goTo(next);
  }

  goTo(index) {
    if (index === this._currentIndex || this._isAnimating) return;
    if (index < 0 || index >= this._slides.length) return;

    if (DESKTOP_MQ.matches) {
      this._fadeTo(index);
    } else {
      this._scrollToSlide(index);
      this._setActiveIndex(index);
    }
  }

  _scrollToSlide(index) {
    const slide = this._slides[index];
    if (!slide || !this._track) return;
    // Use track.scrollTo (not slide.scrollIntoView) so the page doesn't
    // scroll vertically when a dot is tapped. Honor reduced-motion: drop
    // smooth scrolling for users who request reduced motion.
    this._track.scrollTo({
      left: slide.offsetLeft,
      top: 0,
      behavior: REDUCED_MOTION_MQ.matches ? 'auto' : 'smooth',
    });
  }

  _fadeTo(index) {
    const easing = getEasing(EASING_PATH, EASING_FALLBACK);
    const reduced = REDUCED_MOTION_MQ.matches;
    const duration = reduced ? 0 : SLIDE_DURATION;
    const outgoing = this._slides[this._currentIndex];
    const incoming = this._slides[index];

    if (reduced) {
      // No animation; just swap states immediately.
      this._setHeightFromSlide(incoming, { animate: false });
      outgoing.style.opacity = '0';
      incoming.style.opacity = '1';
      this._setActiveIndex(index);
      return;
    }

    this._isAnimating = true;
    incoming.style.opacity = '0';

    Promise.all([
      this._setHeightFromSlide(incoming, { animate: true, duration, easing }),
      outgoing.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration,
        easing,
        fill: 'forwards',
      }).finished,
      incoming.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration,
        easing,
        fill: 'forwards',
      }).finished,
    ]).then(() => {
      outgoing.style.opacity = '0';
      incoming.style.opacity = '1';
      this._isAnimating = false;
    });

    this._setActiveIndex(index);
  }

  _setActiveIndex(index) {
    this._currentIndex = index;
    if (this._dots) {
      this._dots.forEach((dot, i) =>
        dot.classList.toggle(ACTIVE_DOT_CLASS, i === index)
      );
    }
    if (this._dotButtons) {
      this._dotButtons.forEach((btn, i) => {
        if (i === index) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    }
    if (DESKTOP_MQ.matches) {
      this._slides.forEach((slide, i) => {
        this._setSlideHidden(slide, i !== index);
      });
    }
    this._announce(index);
  }

  _announce(index) {
    if (!this._liveRegion) return;
    this._liveRegion.textContent = t('change_announcement', {
      n: index + 1,
      total: this._slides.length,
    });
  }
}

if (!customElements.get('image-slider')) {
  customElements.define('image-slider', ImageSlider);
}
log('[ImageSlider] registered');

export { ImageSlider };
